import React from 'react';
import type { EpicDetail } from '../../../../../../shared/types/epics';
import { StateIndicator } from '../../../../components/shared/StateIndicator';
import { StoryProgressBar } from '../../../../components/shared/StoryProgressBar';
import { PageHeader, StatusBadge } from '../../../../components/ui';
import { formatDate } from '../format';
import { describeState } from '../../../../../../shared/lib/epicStateText';
import { ActionsBar } from './ActionsBar';

// ---------------------------------------------------------------------------
// Header / overview — always visible above the tabs (UX-05).
// ---------------------------------------------------------------------------

export const EpicSummaryHeader: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const isBugfix = epic.kind === 'bugfix';
  const kindLabel = isBugfix ? 'bug fix' : 'epic';
  return (
    <div className="space-y-3">
      <PageHeader
        eyebrow={
          <span className="font-mono flex items-center gap-2">
            <span>
              {epic.repo} · {kindLabel} #{epic.number}
            </span>
            {isBugfix && (
              <StatusBadge tone="warning">🔧 Bug fix</StatusBadge>
            )}
          </span>
        }
        title={epic.title}
        description={
          <span className="text-xs text-slate-500">
            Updated <time dateTime={epic.updatedAt}>{formatDate(epic.updatedAt)}</time>
          </span>
        }
        actions={
          <>
            <StateIndicator state={epic.state} />
            <IssueLink epic={epic} />
            {epic.branch && (
              <span
                className="text-xs text-slate-500 font-mono"
                title="Working branch"
              >
                {epic.branch}
              </span>
            )}
            {epic.prUrl && (
              <a
                href={epic.prUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary-500 hover:text-primary-600 hover:underline"
              >
                PR ↗
              </a>
            )}
          </>
        }
      />
      <p className="text-sm text-slate-700" data-testid="epic-state-text">
        <span>{describeState(epic.state).now}</span>
        {describeState(epic.state).next && (
          <span className="text-slate-500"> Next: {describeState(epic.state).next}</span>
        )}
      </p>
      <ActionsBar epic={epic} />
      <SummaryStrip epic={epic} />
    </div>
  );
};

/**
 * Always-visible link to the underlying GitHub issue. Uses the URL
 * stored on the META row when present; otherwise constructs one from
 * `repo` + `number`, which works for both single-repo and cross-repo
 * epics (cross-repo issues live on the anchor repo, surfaced as `repo`).
 */
export const IssueLink: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  const fallback =
    epic.repo && epic.number > 0
      ? `https://github.com/${epic.repo}/issues/${epic.number}`
      : null;
  const href = epic.github.issueUrl || fallback;
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="epic-issue-link"
      className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline font-medium"
      title="Open the GitHub issue this epic was created from"
    >
      <span aria-hidden>#</span>
      <span>{epic.number}</span>
      <span aria-hidden className="text-slate-400 ml-0.5">on GitHub ↗</span>
    </a>
  );
};

export const SummaryStrip: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  if (epic.kind === 'bugfix') {
    const sessions = epic.sessions.length;
    const running = epic.sessions.filter((s) => s.isRunning).length;
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-card">
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <span>
            {sessions} agent run{sessions === 1 ? '' : 's'}
            {running > 0 && (
              <> · <span className="text-accent-600 font-semibold">{running} running</span></>
            )}
          </span>
          <span>·</span>
          <span className="font-mono tabular-nums">
            ${epic.totalCostUsd.toFixed(4)} spent so far
          </span>
        </div>
      </div>
    );
  }
  if (typeof epic.storyCount !== 'number' || epic.storyCount === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-card">
      <div className="flex items-center gap-3 text-xs text-slate-600">
        <span className="font-semibold text-slate-800">
          {epic.completedStoryCount} / {epic.storyCount} stories complete
        </span>
        <span>·</span>
        <span>
          {epic.sessions.length} agent runs
          {epic.sessions.filter((s) => s.isRunning).length > 0 && (
            <>
              {' '}—{' '}
              <span className="text-accent-600 font-semibold">
                {epic.sessions.filter((s) => s.isRunning).length} running
              </span>
            </>
          )}
        </span>
        {epic.totalCostUsd > 0 && (
          <>
            <span>·</span>
            <span className="font-mono tabular-nums">
              ${epic.totalCostUsd.toFixed(4)}
            </span>
          </>
        )}
      </div>
      <div className="mt-2">
        <StoryProgressBar
          completed={epic.completedStoryCount ?? 0}
          total={epic.storyCount}
        />
      </div>
    </div>
  );
};
